/**
 * ============================================================================
 * config.js — конфигурация приложения «ПротоколЭксперт»
 * ============================================================================
 * Единственное место, где задаются окружение, эндпоинты и бизнес-константы.
 * Разработчикам: при интеграции меняйте значения ЗДЕСЬ, не в коде модулей.
 *
 * Как это грузится (см. index.html): config.js и config.local.js —
 * классические скрипты, кладут window.PE_CONFIG; всё остальное — ES-модули
 * от js/main.js (import/export), которые берут настройки через js/env.js.
 * Разработка — по http (`npm run dev` или `make up`): модули с file:// не
 * грузятся. Контур получает сборку dist/ (scripts/build.js).
 *
 * НАСТРОЙКИ КОНТУРА (USE_MOCKS, API_BASE_URL) задаются НЕ здесь. Они зависят от
 * стенда, а не от кода, и пока лежали константами в этом файле, их правили руками
 * прямо в рабочей копии на сервере — а `git pull` правку затирал (или отказывался
 * обновлять файл с локальными изменениями). Поэтому: значения ниже — умолчания
 * для локальной разработки, а на стенде их перекрывает js/config.local.js. Он
 * подключается сразу после этого файла, в репозиторий не попадает (.gitignore),
 * и потому обновление из репозитория его не трогает. Образец с инструкцией —
 * js/config.local.example.js.
 * ============================================================================
 */
window.PE_CONFIG = {

  /* --- Версия приложения ---------------------------------------------------
   * Semver: MAJOR.MINOR.PATCH[-метка]. Правила подъёма — docs/RELEASE_CHECKLIST.md
   * («Версионирование»). Версии фронта и бэкенда (backend/app/config.py)
   * поднимаются ПАРОЙ в одном коммите + git-тег vX.Y.Z. BUILD_DATE проставляет
   * bundle.py при сборке однофайлового прототипа (здесь null = дев-режим). */
  APP_VERSION: '0.22.3-beta',
  BUILD_DATE: '2026-10-22',

  /* --- Режим работы -------------------------------------------------------
   * true  = фронтенд работает на моках из api.js (демо/разработка UI)
   * false = все вызовы идут на реальный бэкенд по контракту docs/API_CONTRACT.md
   *
   * Умолчание — false (аудит 17.09, Q5). Раньше было true, и стенд, у
   * которого не догрузился или закешировался config.local.js, ТИХО жил на
   * моках: показывал демо-встречи вместо настоящих, а в консоль писал
   * предупреждение, которого никто не читает. Теперь моки — по явному
   * запросу, и в интерфейсе на них виден баннер «ДЕМО» (js/main.js::renderDemoBadge):
   *   - ?mocks=1 в адресе — демо на любом сервере; признак переживает
   *     переходы: hash-роутинг адресную строку не трогает;
   *   - file:// — на случай статической копии; в разработке используйте
   *     `npm run dev` (tests/server.js сам включает моки): ES-модули с
   *     file:// не грузятся;
   *   - USE_MOCKS: true в js/config.local.js — контур-витрина.
   * На боевом стенде config.local.js по-прежнему ставит false явно. */
  USE_MOCKS: (typeof location !== 'undefined')
    && (location.protocol === 'file:' || /[?&]mocks=1(&|$)/.test(location.search)),

  /* --- Базовый URL API ----------------------------------------------------
   * Умолчание — относительный путь: тот же origin, что и страница, поэтому
   * с локальным compose (nginx на :8080 проксирует /v1/ на api) всё работает
   * без правок и без CORS. Все пути в api.js добавляются к этому значению.
   * НА СТЕНДЕ с отдельным доменом API задаётся полный URL в js/config.local.js
   * ('https://api.protocolexpert.ru/v1'), а не здесь (см. шапку файла). */
  API_BASE_URL: '/v1',

  /* Яндекс Метрика экрана входа (FEAT-149, js/metrika.js): счётчик лендинга,
   * грузится только на этих адресах — prod (решение Эда 21.10). Контур может
   * перекрыть в js/config.local.js, например добавить свой адрес для проверки. */
  METRIKA: { id: 113676433, hosts: ['my.protocolexpert.ru'] },

  /* --- Аутентификация -----------------------------------------------------
   * Схема — OTP на почту, пара токенов (см. API_CONTRACT.md § 1): access
   * на час только в памяти вкладки (state.js), refresh в localStorage,
   * обмен и повтор запроса после 401 — api.js (раздел «Сессия»). Аудио
   * плеер берёт по подписанной ссылке GET /meetings/:id/audio-url. */
  AUTH_HEADER: 'Authorization',

  /* --- Тарифы -------------------------------------------------------------
   * Источник правды по ценам — бэкенд (GET /billing/plans, контракт § 5.1):
   * после входа PE_STATE.plans заполняется его ответом, а эти значения —
   * только до загрузки и на случай, если ручка недоступна. Форма — как у
   * сервера (backend/app/deps.py::PLANS): null в minutesPerMonth у платного
   * тарифа и в sessionLimitSec — «без лимита». Тексты по тарифам —
   * js/domain/plans.js. */
  PLANS: {
    trial: { id: 'trial', name: 'Пробный',  priceRub: 0,    periodLabel: '7 дней',
             minutesPerMonth: null, sessionLimitSec: 30 * 60 },
    pro:   { id: 'pro',   name: 'Про',      priceRub: 690,  periodLabel: 'мес',
             minutesPerMonth: 1200, sessionLimitSec: 2 * 3600 },
    max:   { id: 'max',   name: 'Про Макс', priceRub: 1490, periodLabel: 'мес',
             minutesPerMonth: null, sessionLimitSec: 4 * 3600 },
  },

  /* --- Запись -------------------------------------------------------------
   * mimeType подбирается автоматически в recorder.js (Safari не умеет webm).
   * CHUNK_MS — периодичность выдачи аудио-чанков MediaRecorder'ом:
   * чанки можно стримить на бэкенд по мере записи (см. API_CONTRACT § 3.2),
   * чтобы 2-часовая запись не грузилась одним файлом в конце. */
  RECORDING: {
    // Кусок записи уходит в IndexedDB раз в CHUNK_MS (js/rec-store.js): закрытая
    // вкладка или погасший экран на iPhone теряют не больше этих секунд
    CHUNK_MS: 5_000,
    // Свободного места в хранилище браузера меньше — предупреждаем до записи:
    // час записи — около 30–60 МБ, запас на длинную встречу и на очередь
    MIN_FREE_BYTES: 200 * 1024 * 1024,
    // Сердцебиение идущей записи в хранилище и порог, после которого запись
    // без сердцебиения считается оборванной (вкладку закрыли посреди записи)
    BEAT_MS: 5_000,
    STALE_MS: 20_000,
    // Чужие записи (другого аккаунта в этом браузере) старше стольких дней
    // удаляются при входе любого человека — считая от даты самой записи.
    // Свои не удаляются никогда: их отправляют или удаляют руками
    FOREIGN_TTL_DAYS: 30,
    WARN_BEFORE_LIMIT_SEC: 180,   // «осталось 3 минуты» на пробном тарифе
  },

  /* --- Загрузка аудио (контракт § 3.1, § 3.3) ---------------------------
   * Проверяется на клиенте ДО отправки: лимит и белый список повторяют
   * backend/app/config.py::MAX_UPLOAD_BYTES и backend/app/audio_formats.py —
   * отказ должен приходить сразу, а не 413/415 после минуты загрузки.
   * STALL_MS — сколько ждать без единого отправленного байта, прежде чем
   * считать соединение оборванным. */
  /* --- Голосовой ввод контекста (контракт § 3.4) -------------------------
   * MAX_SEC повторяет предел сервера: фрагмент длиннее он отклоняет, поэтому
   * запись останавливаем сами и отдаём сказанное. MAX_BYTES — его же предел
   * на размер: 90 секунд opus в него укладываются с запасом. */
  DICTATION: {
    MAX_SEC: 90,
    MAX_BYTES: 8 * 1024 * 1024,
  },

  UPLOAD: {
    MAX_BYTES: 500 * 1024 * 1024,   // FEAT-110: видео из mp4 упиралось в 200 МБ
    // mov и mkv — видео: сервер берёт из него только звук (FEAT-110)
    FORMATS: ['webm', 'm4a', 'mp4', 'aac', 'caf', '3gp', 'amr', 'mp3', 'wav', 'ogg', 'oga', 'opus', 'flac', 'mov', 'mkv'],
    STALL_MS: 60_000,
    // Запись крупнее — возобновляемой загрузкой кусками (контракт § 3.2):
    // обрыв на середине продолжается с места, а не заново
    RESUMABLE_FROM_BYTES: 10 * 1024 * 1024,
    CHUNK_BYTES: 2 * 1024 * 1024,
  },

  /* --- Клиентский Sentry (js/monitoring.js) ------------------------------
   * Включается только по `sentry` из GET /app/config (DSN, окружение и
   * релиз задаёт бэкенд, SENTRY_DSN_WEB на контуре); здесь — только адрес
   * бандла SDK. Версию поднимать вместе с connect-src/script-src в
   * deploy/headers_app.conf, если сменится домен. */
  MONITORING: {
    SDK_URL: 'https://browser.sentry-cdn.com/10.75.1/bundle.min.js',
  },

  /* --- Оплата (контракт § 5.2, спека 2026-09-21-cloudpayments-design.md) --
   * Скрипт виджета CloudPayments подключается по клику «Подключить», а не на
   * каждой странице. Тариф включает уведомление pay на бэкенде, а не ответ
   * виджета: после «success» фронт опрашивает GET /me каждые ACTIVATION_POLL_MS,
   * пока план не сменится, не дольше ACTIVATION_TIMEOUT_MS. */
  BILLING: {
    WIDGET_URL: 'https://widget.cloudpayments.ru/bundles/cloudpayments.js',
    ACTIVATION_POLL_MS: 2500,
    ACTIVATION_TIMEOUT_MS: 30_000,
  },

  /* --- Список встреч (контракт § 2.1) ------------------------------------
   * Главный экран грузит по MEETINGS_PAGE_SIZE строк, дальше — «Показать
   * ещё» по X-Next-Cursor (сервер допускает 1…200). */
  MEETINGS_PAGE_SIZE: 50,

  /* --- Опрос статуса обработки -------------------------------------------
   * UI опрашивает GET /meetings/:id/status, пока status не ready/failed:
   * первый интервал POLL_MS, дальше растёт до POLL_MAX_MS (3 → 5 → 9 → 15 с).
   * В скрытой вкладке опрос замирает. ИНТЕГРАЦИЯ: при желании замените на
   * SSE/WebSocket — точка одна: api.js::meetings.waitReady(). */
  PROCESSING_POLL_MS: 3000,
  PROCESSING_POLL_MAX_MS: 15_000,
  /* Сколько всего ждать готовности протокола, прежде чем сказать человеку
     «идёт дольше обычного» и отпустить его. Сервер параллельно переводит
     зависшие обработки в failed (watchdog, 30 минут). */
  PROCESSING_TIMEOUT_MS: 15 * 60 * 1000,

  /* --- ИИ-чат (контракт § 4) --------------------------------------------
   * Ответ считает воркер: POST /chat ждёт до 25 с, иначе 202 и опрос
   * сообщения каждые CHAT_POLL_MS. Сколько всего ждать ответа. */
  CHAT_POLL_MS: 1500,
  CHAT_TIMEOUT_MS: 3 * 60 * 1000,

  /* --- Прочее ------------------------------------------------------------- */
  // Бот Telegram — из /app/config urls.support (FEAT-119), своего имени у клиента
  // нет. Предел файла для бота — как TELEGRAM_IMPORT_MAX_MB на сервере: больше
  // облачный Bot API ботам не отдаёт
  TELEGRAM_IMPORT_MAX_MB: 20,
  // Бот MAX — из /app/config urls.maxBot (FEAT-127). Предел файла — как
  // MAX_IMPORT_MAX_MB на сервере (тот же, что у загрузки на сайте)
  MAX_IMPORT_MAX_MB: 500,
  SUPPORT_EMAIL: 'help@protocolexpert.ru',
  SUPPORT_TG_URL: 'https://t.me/ProtokolExpert_support',      // ИНТЕГРАЦИЯ: чат поддержки
  /* Публичные ссылки «Поделиться» (страница /p/:token отдаёт бэкенд, § 7).
   * В моках ссылка собирается из этого префикса — просто для копирования. */
  SHARE_BASE_URL: 'https://protocolexpert.ru/p/',
};
